import { useState } from 'react';
import { categories, mainSiteUrl } from '@/mocks/categories';

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const url = e.target.value;
    if (url) {
      setSelectedCategory(url);
      window.open(url, '_blank');
      e.target.value = '';
    }
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md bg-background-50/70 border-b border-background-200/60">
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <a
            href="/"
            className="flex items-center gap-2 shrink-0"
          >
            <div className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded-lg bg-primary-500 text-background-50">
              <i className="ri-file-list-3-line text-lg"></i>
            </div>
            <span className="font-heading text-lg md:text-xl font-semibold text-foreground-950 whitespace-nowrap">
              Superbuy Spread
            </span>
          </a>

          <div className="hidden md:flex items-center gap-3 flex-1 justify-center mx-6">
            <div className="relative w-full max-w-xs">
              <select
                value={selectedCategory}
                onChange={handleCategoryChange}
                className="w-full appearance-none bg-background-50 border border-background-200 rounded-xl px-4 py-2.5 text-sm text-foreground-700 cursor-pointer focus:outline-none focus:border-primary-300 transition-colors font-body"
              >
                <option value="" disabled>
                  Browse Categories...
                </option>
                {categories.map((cat) => (
                  <option key={cat.slug} value={cat.url}>
                    {cat.name}
                  </option>
                ))}
              </select>
              <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-down-s-line text-foreground-400 text-sm"></i>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-foreground-300 text-foreground-700 text-sm font-medium hover:border-foreground-500 hover:text-foreground-950 transition-colors whitespace-nowrap"
            >
              Superbuy Sheet
              <div className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-arrow-right-up-line text-xs"></i>
              </div>
            </a>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              Buy It Now
              <div className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-arrow-right-up-line text-xs"></i>
              </div>
            </a>
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="md:hidden w-9 h-9 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors"
              aria-label="Toggle menu"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={`text-lg ${isOpen ? 'ri-close-line' : 'ri-menu-line'} text-foreground-700`}></i>
              </div>
            </button>
          </div>
        </div>

        {isOpen && (
          <div className="md:hidden pb-5 border-t border-background-200/60 pt-4 animate-in fade-in slide-in-from-top-2 duration-200">
            <div className="mb-4">
              <h4 className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-3 px-1">
                Product Categories
              </h4>
              <div className="grid grid-cols-2 gap-2">
                {categories.map((cat) => (
                  <a
                    key={cat.slug}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setIsOpen(false)}
                    className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl bg-background-100 hover:bg-background-200/70 transition-colors cursor-pointer group"
                  >
                    <div className="w-8 h-8 shrink-0 rounded-lg flex items-center justify-center bg-background-50 group-hover:bg-background-50 transition-colors">
                      <i className="ri-shopping-bag-3-line text-foreground-500 text-sm"></i>
                    </div>
                    <span className="text-xs font-medium text-foreground-700 group-hover:text-foreground-950 transition-colors leading-tight">
                      {cat.name}
                    </span>
                  </a>
                ))}
              </div>
            </div>

            <div className="border-t border-background-200/60 pt-4 mb-3">
              <h4 className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-3 px-1">
                Shopping Guides
              </h4>
              <div className="space-y-1">
                <a
                  href="/superbuy-spreadsheet-guide"
                  onClick={() => setIsOpen(false)}
                  className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <div className="w-8 h-8 shrink-0 rounded-lg flex items-center justify-center bg-accent-100">
                    <i className="ri-book-open-line text-accent-600 text-sm"></i>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-foreground-800 block">Complete Guide</span>
                    <span className="text-xs text-foreground-500">Master the spreadsheet workflow</span>
                  </div>
                </a>
                <a
                  href="/qc-guide"
                  onClick={() => setIsOpen(false)}
                  className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <div className="w-8 h-8 shrink-0 rounded-lg flex items-center justify-center bg-accent-100">
                    <i className="ri-zoom-in-line text-accent-600 text-sm"></i>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-foreground-800 block">QC Guide</span>
                    <span className="text-xs text-foreground-500">Inspect products like a pro</span>
                  </div>
                </a>
                <a
                  href="/safety-guide"
                  onClick={() => setIsOpen(false)}
                  className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <div className="w-8 h-8 shrink-0 rounded-lg flex items-center justify-center bg-accent-100">
                    <i className="ri-shield-check-line text-accent-600 text-sm"></i>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-foreground-800 block">Safety Guide</span>
                    <span className="text-xs text-foreground-500">Shop with confidence</span>
                  </div>
                </a>
              </div>
            </div>

            <div className="border-t border-background-200/60 pt-4">
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setIsOpen(false)}
                className="flex items-center justify-center gap-2 w-full px-4 py-3 rounded-full border border-foreground-300 text-foreground-700 text-sm font-medium hover:border-foreground-500 hover:text-foreground-950 transition-colors whitespace-nowrap mb-2"
              >
                Superbuy Sheet
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-up-line text-sm"></i>
                </div>
              </a>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setIsOpen(false)}
                className="flex items-center justify-center gap-2 w-full px-4 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                Buy It Now
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-up-line text-sm"></i>
                </div>
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}